import { useState, useEffect, useCallback, useRef } from "react";
import config from "../config";
import { useDataCache, CACHE_KEYS } from "../contexts/DataCacheContext";
import { isMasterAdmin } from "../utils/auth";

const DEFAULT_DATA = {
  totalAvoided: 0,
  totalEnergyKwh: 0,
  totalWasteStageQty: 0,
  rolling30: {
    current: 0,
    previous: 0,
    percentChange: 0,
    deliveryCount: 0,
  },
  avgPerDelivery: 0,
  totalDeliveries: 0,
  topMaterial: { name: "-", percentage: 0, avoided: 0 },
  materialBreakdown: [],
  receiverBreakdown: [],
  goal: { target: 0, percentage: 0 },
};

export const useDonorGHGData = (filters = {}) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [data, setData] = useState(DEFAULT_DATA);
  const [userGoals, setUserGoals] = useState({}); // empty by default, no fallbacks
  const [goalsUpdatedAt, setGoalsUpdatedAt] = useState(null); // when goals were last saved

  const { getCachedData, setCachedData } = useDataCache();

  const isFetching = useRef(false);
  const isInitialLoad = useRef(true);

  // fetch user's GHG goals from profile
  const fetchUserGoals = useCallback(async () => {
    // first check if we have cached goals from profile page
    const cachedGoals = getCachedData(CACHE_KEYS.PROFILE_GHG_GOALS);
    if (cachedGoals?.data?.goals) {
      setUserGoals(cachedGoals.data.goals);
      setGoalsUpdatedAt(cachedGoals.data.goalsUpdatedAt || null);
      return cachedGoals.data.goals;
    }

    // otherwise fetch from API
    try {
      const response = await fetch(`${config.apiUrl}/user/ghg-goals`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        credentials: "include",
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.goals) {
          setUserGoals(result.goals);
          setGoalsUpdatedAt(result.updated_at || null);
          return result.goals;
        }
      }
    } catch (err) {
      console.warn("Failed to fetch user GHG goals:", err);
    }

    return {};
  }, [getCachedData]);

  const fetchData = useCallback(
    async (filterParams = {}) => {
      if (isFetching.current) return;
      isFetching.current = true;

      if (!isInitialLoad.current) {
        setRefreshing(true);
      }

      try {
        // fetch goals and GHG data in parallel
        await Promise.all([fetchUserGoals()]);

        const params = new URLSearchParams();

        if (filterParams.dateFrom) {
          params.append("date_from", filterParams.dateFrom);
        }
        if (filterParams.dateTo) {
          params.append("date_to", filterParams.dateTo);
        }
        if (isMasterAdmin()) {
          params.append("show_all", "true");
        }

        const url = `${config.apiUrl}/donor/ghg/summary?${params.toString()}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          credentials: "include",
        });

        const result = await response.json();

        if (response.ok && result.success) {
          const newData = {
            totalAvoided: result.totalAvoided || 0,
            totalEnergyKwh: result.totalEnergyKwh || 0,
            totalWasteStageQty: result.totalWasteStageQty || 0,
            rolling30: result.rolling30 || DEFAULT_DATA.rolling30,
            avgPerDelivery: result.avgPerDelivery || 0,
            totalDeliveries: result.totalDeliveries || 0,
            topMaterial: result.topMaterial || DEFAULT_DATA.topMaterial,
            materialBreakdown: result.materialBreakdown || [],
            receiverBreakdown: result.receiverBreakdown || [],
            goal: result.goal || DEFAULT_DATA.goal,
          };
          setData(newData);
          setError(null);
          const timestamp = Date.now();
          setLastUpdated(timestamp);
          setCachedData(CACHE_KEYS.DONOR_GHG, newData, timestamp);
        } else {
          setError(result.message || "Failed to load GHG data");
        }
      } catch (err) {
        console.error("Error fetching donor GHG data:", err);
        setError("Unable to connect to server");
      } finally {
        setLoading(false);
        setRefreshing(false);
        isFetching.current = false;
        isInitialLoad.current = false;
      }
    },
    [setCachedData, fetchUserGoals],
  );

  useEffect(() => {
    const contextCache = getCachedData(CACHE_KEYS.DONOR_GHG);
    if (contextCache && contextCache.data) {
      setData(contextCache.data);
      setLastUpdated(contextCache.timestamp);
      setLoading(false);
      isInitialLoad.current = false;

      // still fetch goals even if we have cached GHG data
      fetchUserGoals();
      return;
    }

    fetchData(filters);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = useCallback(
    (newFilters = filters) => {
      setRefreshing(true);
      fetchData(newFilters);
    },
    [fetchData, filters],
  );

  // helper to get goal for a specific year (returns null if no goal set)
  const getYearlyGoal = useCallback(
    (year) => {
      return userGoals[year] || null;
    },
    [userGoals],
  );

  return {
    loading,
    refreshing,
    error,
    data,
    refresh,
    lastUpdated,
    userGoals,
    goalsUpdatedAt,
    getYearlyGoal,
    ...data, // spread for convenience
  };
};

export default useDonorGHGData;
